import { useQuery } from "@tanstack/react-query";
import React from "react";
import { Page } from "../activities/AppPage";
import { API } from "../API";
import { ModalActions, useModalV2 } from "../components/AppModals";
import { getComplianceBannerDetails } from "../components/ComplianceBanner/complianceBannerUtils";
import { useMedflytAgencyId } from "../components/withMedflytAgencyId";
import { isLoading } from "../Loading";
import { AgencyId, CaregiverStatus } from "../messages";
import { STRINGS } from "../strings";
import { AppState } from "../system/AppState";
import { translate } from "../translate";
import useTrainingCenterState from "./useTrainingCenterState";
import { default as DoctorIllustrationWithBg } from "/public/assets/doctor-illustration-with-bg.svg?react";

export default function useComplianceIssuesModal(props: {
  caregiverStatusInMedflyt: CaregiverStatus | null;
  api: Pick<API, "getComplianceItems">;
  appState: AppState;
  onRequestNavigate: (page: Page) => void;
}) {
  const { createModal } = useModalV2();
  const medflytAgencyId = useMedflytAgencyId();

  const caregiverCompliance = useQuery({
    queryKey: ["compliance_items", medflytAgencyId],
    queryFn: () => props.api.getComplianceItems(medflytAgencyId as AgencyId),
    enabled: !isLoading(medflytAgencyId),
  });

  const trainingCenterState = useTrainingCenterState(props.appState);

  const complianceBannerDetails = getComplianceBannerDetails(
    caregiverCompliance.data?.items ?? [],
    isLoading(trainingCenterState) ? [] : trainingCenterState.bundles.bundles.toArray()
  );

  const getHasIssues = () => {
    if (complianceBannerDetails.message === null || props.caregiverStatusInMedflyt === null) {
      return false;
    }

    return !["PENDING", "REJECTED", "TERMINATED"].includes(props.caregiverStatusInMedflyt);
  };

  const openModal = () => {
    const { title, message, multipleComplianceItems } = complianceBannerDetails;

    if (message === null) {
      return;
    }

    createModal({
      type: "info",
      icon: <DoctorIllustrationWithBg style={{ marginTop: -70 }} />,
      title: title,
      message: message,
      actions: [
        ({ close }) => ({
          key: "confirm",
          children: multipleComplianceItems
            ? translate(STRINGS.FIX_ISSUES)
            : translate(STRINGS.FIX_ISSUE),
          onClick: () => {
            props.onRequestNavigate({ type: "MyMedflytCompliance" });
            close();
          },
        }),
        ModalActions.Close(),
      ],
      options: {
        messageAsHtml: true,
      },
    });
  };

  return {
    complianceBannerDetails,
    hasIssues: getHasIssues(),
    openModal,
  };
}
